import React, { ReactNode, useEffect, useRef } from "react";
import { btn } from "../styles/buttonStyles";

type ConfirmActionDialogProps = {
  open: boolean;
  title: string;
  description: string;
  children: ReactNode;
  confirmLabel: string;
  // Red confirm button for actions that delete or stop something
  destructive?: boolean;
  confirmDisabled?: boolean;
  // Info-only dialogs show just the confirm button
  hideCancel?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
};

const ConfirmActionDialog = ({
  open,
  title,
  description,
  children,
  confirmLabel,
  destructive = false,
  confirmDisabled = false,
  hideCancel = false,
  onConfirm,
  onCancel,
}: ConfirmActionDialogProps) => {
  const dialogRef = useRef<HTMLDivElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const confirmRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) {
      return;
    }

    // Start on the confirmation field if there is one, else Cancel so Enter never confirms by accident
    (dialogRef.current?.querySelector("input") ?? cancelRef.current ?? confirmRef.current)?.focus();
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onCancel();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, onCancel]);

  if (!open) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
      <div
        ref={dialogRef}
        className="w-full max-w-lg rounded-sm border border-gray-200 bg-white shadow-xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-action-title"
      >
        <div className="border-b border-gray-200 px-4 py-3">
          <h3 id="confirm-action-title" className="text-base font-semibold text-gray-900">{title}</h3>
          <p className="mt-1 text-sm text-gray-600">{description}</p>
        </div>

        <div className="px-4 py-3">{children}</div>

        <div className="flex justify-end gap-2 border-t border-gray-200 px-4 py-3">
          {!hideCancel && (
            <button ref={cancelRef} type="button" className={btn.secondarySmall} onClick={onCancel}>
              Cancel
            </button>
          )}
          <button ref={confirmRef} type="button" disabled={confirmDisabled} className={destructive ? btn.destructiveSmall : btn.applySmall} onClick={onConfirm}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmActionDialog;
